import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, Output } from '@angular/core';
import { ImageFetchPriority } from '@shared/models';
import { Image, ImageGroup } from '@spartacus/core';
import { ImageLoadingStrategy, Media, MediaContainer, MediaService } from '@spartacus/storefront';

@Component({
  selector: 'sd-media',
  templateUrl: './media.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomMediaComponent implements OnChanges {
  /**
   * The media container can hold multiple media items, so that
   * a specific media (by format) can be used or multiple media
   * can be provided in a `srcset` so the browser will figure out
   * the best media for the device.
   */
  @Input() container: MediaContainer | Image | ImageGroup | ImageGroup[] | undefined;

  /**
   * if a media format is given, a media for the given format will be rendered
   */
  @Input() format: string;

  /**
   * A specific alt text for an image, which overrules the alt text
   * from the container data.
   */
  @Input() alt: string;

  /**
   * set role of the image if different than what is intended (eg, role="presentation")
   */
  @Input() role: string;

  /**
   * Set the loading strategy of the media. Defaults to global loading strategy.
   * Use 'lazy' or 'eager' strategies.
   */
  @Input() loading: ImageLoadingStrategy | null = ImageLoadingStrategy.LAZY;

  @Input() fetchpriority: ImageFetchPriority = ImageFetchPriority.AUTO;

  @Input() width?: number;

  @Input() height?: number;

  /**
   * Картинка по умолчанию для товара
   */
  @Input() defaultImage = 'assets/images/default-product-image.svg';

  /**
   * Once the media is loaded, we emit an event.
   */
  @Output() loaded: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * The media contains the info for the UI to create the image. This media
   * object might contain more info once other media types (i.e. video) is supported.
   */
  media: Media | undefined;

  /** Использовать картинку по умолчанию */
  isUsingDefaultImage = false;

  /**
   * The `cx-media` component has an `is-initialized` class as long as the
   * media is being initialized.
   */
  @HostBinding('class.is-initialized') isInitialized = false;

  /**
   * The `cx-media` component has a `is-loading` class as long as the
   * media is loaded. Wehn the media is loaded, the `is-initialized` class
   * is added.
   */
  @HostBinding('class.is-loading') isLoading = true;

  /**
   * When there's no media provided for the content, or in case an error
   * happened during loading, we add the `is-missing` class. Visual effects
   * can be controlled by CSS.
   */
  @HostBinding('class.is-missing') isMissing = false;

  constructor(protected mediaService: MediaService) {}

  ngOnChanges(): void {
    this.create();
  }

  /**
   * Whenever an error happens during load, we mark the component
   * with css classes to have a missing media.
   */
  errorHandler(): void {
    if (!this.isUsingDefaultImage && this.defaultImage) {
      this.isUsingDefaultImage = true;
      this.media = {
        ...this.media,
        src: this.defaultImage,
        srcset: undefined,
      };
    } else {
      this.handleMissing();
    }
  }

  /**
   * This handler is called from the UI when the image is loaded.
   */
  loadHandler(): void {
    this.isLoading = false;
    this.isInitialized = true;
    this.isMissing = false;
    this.loaded.emit(true);
  }

  /**
   * Creates the `Media` object
   */
  protected create(): void {
    this.isUsingDefaultImage = false;
    this.media = this.mediaService.getMedia(
      this.container instanceof Array ? this.container[0] : this.container,
      this.format,
      this.alt,
      this.role,
    );
    if (!this.media?.src) {
      this.handleMissing();
    }
  }

  protected handleMissing(): void {
    this.isLoading = false;
    this.isInitialized = true;
    if (this.defaultImage) {
      this.isUsingDefaultImage = true;
      this.isMissing = false;
      this.media = {
        ...this.media,
        src: this.defaultImage,
        srcset: undefined,
        alt: this.alt ?? this.media?.alt ?? '',
        role: this.media?.role,
      };
    } else {
      this.isMissing = true;
    }
    this.loaded.emit(false);
  }
}
